import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { observer } from 'mobx-react-lite';
import socket from '../utils/socket';
import type { RealtimePacket } from '../utils/socket';
import { useAuthStore, useRequestStore, useShiftHistoryStore, useManagerQueueStore } from '../stores/root_store';
import type { FHRequestMessage, ShiftHistoryItem } from '../types';

type ConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'identified'
  | 'error';

interface RealtimeContextType {
  connectionState: ConnectionState;
  socketId: string | null;
}

const RealtimeContext = createContext<RealtimeContextType>({
  connectionState: 'disconnected',
  socketId: null,
});

// eslint-disable-next-line react-refresh/only-export-components
export const useRealtime = () => useContext(RealtimeContext);

interface RealtimeProviderProps {
  children: React.ReactNode;
}

const RealtimeProviderBase: React.FC<RealtimeProviderProps> = ({ children }) => {
  const authStore = useAuthStore();
  const requestStore = useRequestStore();
  const shiftHistoryStore = useShiftHistoryStore();
  const managerQueueStore = useManagerQueueStore();
  const [connectionState, setConnectionState] =
    useState<ConnectionState>('disconnected');
  const [socketId, setSocketId] = useState<string | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);

  const { token, user } = authStore;
  const userUuid = user?.uuid;

  // Handle incoming realtime updates
  const handlePublished = useCallback(
    (data: RealtimePacket) => {
      const { meta, payload } = data;

      console.log('[Realtime] Received:', meta.type, payload);

      switch (meta.type) {
        case 'request_message': {
          // Handle new message
          const message = payload as unknown as FHRequestMessage;
          if (message.request) {
            // message.request is the request UUID
            requestStore.receiveMessageUpdate(message.request, message);
          }
          break;
        }

        case 'request': {
          // Handle request update (status change, etc.)
          const updatedRequest = payload as unknown as import('../types').FHRequest;
          if (updatedRequest.key) {
            console.log('[Realtime] Request update:', updatedRequest.key, 'status:', updatedRequest.status);
            requestStore.receiveRequestUpdate(updatedRequest);
          }
          break;
        }

        case 'shift_history': {
          // Handle shift history update (for managers)
          const historyItem = payload as unknown as ShiftHistoryItem;
          console.log('[Realtime] Shift history:', historyItem.associated_type);
          shiftHistoryStore.receiveUpdate(historyItem);

          // If this is a help request, refresh the manager queue
          if (historyItem.associated_type === 'assistant:help-request') {
            console.log('[Realtime] Refreshing manager queue for help request');
            managerQueueStore.fetchQueue(true);
          }
          break;
        }


        default:
          console.log('[Realtime] Unhandled type:', meta.type);
      }
    },
    [requestStore, shiftHistoryStore, managerQueueStore]
  );

  // Connect to socket
  const connect = useCallback(() => {
    if (!token || !userUuid) {
      console.log('[Realtime] Missing token or user, not connecting');
      return;
    }

    console.log('[Realtime] Connecting...');
    setConnectionState('connecting');

    // Set the token in socket query params
    if (socket.io.opts.query) {
      (socket.io.opts.query as Record<string, string>).morty_token = token;
    }

    socket.connect();
  }, [token, userUuid]);

  // Setup socket event handlers
  useEffect(() => {
    if (!token || !userUuid) {
      return;
    }

    const onConnect = () => {
      console.log('[Realtime] Connected, identifying...');
      setConnectionState('connected');
      setSocketId(socket.id || null);
      reconnectAttemptsRef.current = 0;

      // Send identify event with user_uuid and role
      socket.emit('identify', {
        user_uuid: userUuid,
        role: user?.is_beta ? 'assistant' : 'customer',
      });
    };

    const onIdentified = (connectionId: string) => {
      console.log('[Realtime] Identified:', connectionId);
      setConnectionState('identified');

      // If user is a manager, subscribe to support channels for team activity
      if (user?.is_manager) {
        socket.emit('join', { channel: 'support/shift_history' });
        console.log('[Realtime] Joined support/shift_history channel');
      }
    };

    const onDisconnect = (reason: string) => {
      console.log('[Realtime] Disconnected:', reason);
      setConnectionState('disconnected');
      setSocketId(null);

      // Auto-reconnect with exponential backoff
      if (reason !== 'io client disconnect') {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        reconnectAttemptsRef.current += 1;

        console.log(`[Realtime] Reconnecting in ${delay}ms...`);
        reconnectTimeoutRef.current = window.setTimeout(() => {
          connect();
        }, delay);
      }
    };

    const onConnectError = (error: Error) => {
      console.error('[Realtime] Connection error:', error);
      setConnectionState('error');
    };

    // Register event handlers
    socket.on('connect', onConnect);
    socket.on('identified', onIdentified);
    socket.on('published', handlePublished);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);

    // Initial connection (deferred to avoid synchronous setState in effect)
    let initialConnectTimeout: number | null = null;
    if (!socket.connected) {
      initialConnectTimeout = window.setTimeout(() => connect(), 0);
    }

    // Cleanup
    return () => {
      if (initialConnectTimeout) {
        clearTimeout(initialConnectTimeout);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      socket.off('connect', onConnect);
      socket.off('identified', onIdentified);
      socket.off('published', handlePublished);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.disconnect();
    };
  }, [token, userUuid, user, connect, handlePublished]);

  const value: RealtimeContextType = {
    connectionState,
    socketId,
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const RealtimeProvider = observer(RealtimeProviderBase);
